import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { User } from '../../types/user';
import Checkmark from '../../static/img/feedback-success.svg';
import ArrowRight from '../../static/img/icon-open-menu.svg';
import CheckList from '../../static/img/check_list.svg';
import environmentConfigs from '../../config/environment-configs';
import { DeleteAccountContext } from '.';
import Icon from '../../components/Section/Icon';
import { LinkWithRoleButton } from '../../components/Link/LinkWithRoleButton';
import { Actions, Categories, Labels } from '../../types/gtm';

interface UserProps {
  user: User;
}

const SuccessMessageContainer = styled.div`
  width: 516px;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 64px;
  text-align: left;
  font-size: 18px;
  line-height: 150%;
  color: #333333;

  > p:first-of-type {
    margin-bottom: 24px;
    margin-top: 32px;
  }

  @media (max-width: 650px) {
    margin-top: 40px;
    width: 95%;
    text-align: center;
  }
`;

const DivCustom = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  > img {
    width: 100px;
    margin-right: 24px;
  }

  h1 {
    font-size: 28px;
    margin: 0;
    line-height: 100%;
  }

  strong {
    font-weight: 700;
  }

  @media (max-width: 650px) {
    flex-direction: column;

    > img {
      margin-bottom: 16px;
      margin-right: 0px;
    }
  }
`;

const Box = styled.div`
  margin-top: 32px;
  text-align: left;

  > p:first-of-type {
    margin-bottom: 32px;
  }

  > h3 {
    margin-bottom: 24px;
  }
`;

const ContainerLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 24px;
  margin-bottom: 0;

  > a {
    font-size: 14px;
    text-decoration: none;
    text-align: left;
    font-weight: 600;
  }

  > img {
    margin-left: 19px;
  }

  @media (max-width: 650px) {
    max-width: 270px;
  }
`;

const ContainerList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: 24px;
  margin-bottom: 0;

  > div {
    display: flex;

    > p {
      font-size: 14px;
      line-height: 150%;
      margin: 0;
      margin-left: 10px;
    }
  }
`;

const DeleteSuccess = ({ user }: UserProps) => {
  const { quarantineDays } = useContext(DeleteAccountContext);

  window.dataLayer = window.dataLayer || [];

  const eventToReactivatedLink = () => {
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.DeleteAccount,
      event_action: Actions.ClickReactivateAccount,
      event_label: Labels.DeleteAccountPage,
      bd_suser_code: user?.globoId,
    });
  };

  const eventToAboutLink = () => {
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.DeleteAccount,
      event_action: Actions.ClickAboutContaGlobo,
      event_label: Labels.DeleteAccountPage,
      bd_suser_code: user?.globoId,
    });
  };

  useEffect(() => {
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.DeleteAccount,
      event_action: 'Sucesso',
      event_label: Labels.DeleteAccountPage,
      bd_suser_code: user?.globoId,
    });
  }, [user]);

  return (
    <SuccessMessageContainer>
      <DivCustom>
        <Icon svg={Checkmark} alt="success" />
        <h1>Solicitação de exclusão realizada com sucesso</h1>
      </DivCustom>
      <p>
        Enviamos um e-mail para <strong>{user.email}</strong> com os dados do
        seu pedido.
      </p>
      <p>
        Pode ser que você ainda receba alguns comunicados sobre a sua Conta
        Globo por e-mail durante os próximos {quarantineDays} dias.
      </p>

      <Box className="mcv3-card">
        <h3>Mudou de ideia?</h3>
        <p>
          Caso tenha excluído sua conta por engano ou mude de ideia, ainda
          possível reativá-la. Basta fazer login em até {quarantineDays} dias e
          o processo de de exclusão será cancelado automaticamente.
        </p>
        <p>
          <strong>Importante:</strong> Talvez nem todos os dados possam ser
          recuperados, pois o processo de exclusão de conta já pode ter sido
          iniciado.
        </p>

        <ContainerLink>
          <LinkWithRoleButton
            href={environmentConfigs.ACCOUNT_MANAGEMENT_DEFAULT_DNS_URL}
            onClick={() => eventToReactivatedLink()}
          >
            Quero reativar minha Conta Globo
          </LinkWithRoleButton>
          <Icon svg={ArrowRight} alt="abertura sobre a mesma página" />
        </ContainerLink>

        <ContainerList>
          <div>
            <Icon svg={CheckList} alt="identificador de lista" />
            <p>A Conta Globo é gratuita, basta se cadastrar e acessar!</p>
          </div>
          <div>
            <Icon svg={CheckList} alt="identificador de lista" />
            <p>Use o mesmo login para todos os produtos Globo e parceiros.</p>
          </div>
        </ContainerList>

        <ContainerLink>
          <LinkWithRoleButton
            href={environmentConfigs.ACCOUNT_GLOBO_INFO_URL}
            onClick={() => eventToAboutLink()}
          >
            Conheça todos os benefícios da Conta Globo
          </LinkWithRoleButton>
          <Icon svg={ArrowRight} alt="abertura sobre a mesma página" />
        </ContainerLink>
      </Box>
    </SuccessMessageContainer>
  );
};

export default DeleteSuccess;
